import {AfterViewInit, computed, Directive, effect, inject, Injector, OnInit} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {applyTransaction} from '@datorama/akita';
import {Actions} from '@ngneat/effects-ng';
import {SortDescriptor} from '@progress/kendo-data-query';
import {SpaceSettingsService} from '@q9elements/ui-kit/common';
import {
  FilterDescriptors,
  GridColumn,
  GridQuery,
  GridStore,
  loadEntities
} from '@q9elements/ui-kit/components';
import {differenceWith, find, get, isEqual, unionBy} from 'lodash';

import {FEEDBACK_TABS} from '../../../../../feedbacks/models/feedback';
import {DEFAULT_VIEW_ID} from '../../../grid-views/models/constants';
import {GridViewsService} from '../../../grid-views/services/grid-views.service';

@Directive()
export abstract class GridWrapperComponent implements OnInit, AfterViewInit {
  protected actions = inject(Actions);
  protected injector = inject(Injector);
  protected store = inject(GridStore);
  protected query = inject(GridQuery);
  protected route = inject(ActivatedRoute);
  protected gridViewsService = inject(GridViewsService, {optional: true});
  private router = inject(Router);
  private spaceSettingsService = inject(SpaceSettingsService);

  public feedbackState: FEEDBACK_TABS | undefined;

  protected readonly filters = this.query.selectFilters();
  protected readonly loading = toSignal<boolean>(this.query.selectLoading(), {requireSync: true});
  protected readonly entities = toSignal(this.query.selectAll(), {requireSync: true});
  protected readonly showGeneralPlaceholder = computed(
    () => !this.entities().length && !this.filters().length
  );

  readonly columns = computed(() => {
    const allColumns = this.query.getColumns();
    const selectedView = this.gridViewsService?.selectedView();

    if (selectedView) {
      return allColumns.filter(col => selectedView.columns.includes(col.field));
    }

    return allColumns;
  });

  readonly canResetFilters = computed(() => {
    const allFilters = this.filters();
    const userFilters = this.distinguishGridFilters(allFilters);

    return userFilters.length > 0;
  });

  abstract getColumns(): GridColumn[];

  private get moduleType() {
    return this.store.storeName;
  }

  ngOnInit() {
    this.setColumns();
  }

  ngAfterViewInit() {
    if (this.gridViewsService) {
      this.initGridViewObserver();
    } else {
      this.updateFiltersAndLoadEntities();
    }
  }

  onGridStateChanged(event: {filters?: FilterDescriptors; sort?: SortDescriptor[]}) {
    const sort = event.sort;
    const filters = this.distinguishGridFilters(event.filters!);
    const path = this.getPath();

    this.spaceSettingsService.updateSettingsByKey(path, {sort, filters});
  }

  onEntityUpdate(id: string, payload: any) {
    const entityId = get(payload, 'id', id);

    this.store.update(entityId, payload);
  }

  public onStateChanged(state: FEEDBACK_TABS) {
    this.feedbackState = state;
    this.updateFiltersAndLoadEntities();
  }

  public resetQueryParams() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });
  }

  protected getSelectedViewId(): string {
    return this.spaceSettingsService.getSettingsByKey(
      `${this.moduleType}.gridViewId`,
      DEFAULT_VIEW_ID
    );
  }

  protected getFilters(): FilterDescriptors {
    const gridViewFilters = this.gridViewsService?.selectedView()?.filters || [];
    const storedFilters = this.getStoredFilters();

    return unionBy(storedFilters, gridViewFilters, 'field');
  }

  private getStoredFilters(): FilterDescriptors {
    const path = this.getPath() + '.filters';

    return this.spaceSettingsService.getSettingsByKey(path) || [];
  }

  protected getSort(): SortDescriptor[] {
    const path = this.getPath() + '.sort';

    return this.spaceSettingsService.getSettingsByKey(path) || [];
  }

  private setColumns() {
    const columns = this.getColumns();
    const columnWidth: {
      [field: string]: number;
    } = this.spaceSettingsService.getSettingsByKey(`${this.moduleType}.columnWidth`);

    if (columnWidth) {
      for (const [field, width] of Object.entries(columnWidth)) {
        const column = find(columns, {field});

        if (column) {
          column.width = width;
        }
      }
    }

    return this.store.update({columns});
  }

  private initGridViewObserver() {
    const gridViewsEffect = effect(
      () => {
        const hasGridViews = this.gridViewsService!.views().length;

        if (!hasGridViews) {
          return void 0;
        }

        const id = this.getSelectedViewId();

        this.gridViewsService!.selectView(id);

        this.updateFiltersAndLoadEntities();

        gridViewsEffect.destroy();
      },
      {injector: this.injector, allowSignalWrites: true}
    );
  }

  private updateFiltersAndLoadEntities() {
    const filters = this.getFilters();
    const sort = this.getSort();

    applyTransaction(() => {
      this.store.update({filters});

      if (sort?.length) {
        this.store.update({sort});
      }
    });

    this.actions.dispatch(loadEntities({reset: true}));
  }

  private distinguishGridFilters(allFilters: FilterDescriptors): FilterDescriptors {
    const gridViewFilters = this.gridViewsService?.selectedView()?.filters || [];

    return differenceWith(allFilters, gridViewFilters, isEqual);
  }

  private getPath() {
    const viewId = this.gridViewsService?.selectedViewId() || DEFAULT_VIEW_ID;
    const statePath = this.feedbackState ? `.${this.feedbackState}` : '';

    return `${this.moduleType}.${viewId}.grid${statePath}`;
  }
}
